import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  List as MuiList,
  ListItem,
  ListSubheader,
  ListItemText,
  Divider,
  makeStyles,
  createStyles,
  Typography,
  LinearProgress,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { useHistory, useLocation } from "react-router-dom";

import { get日付 } from "../../helper/JapanDate";
import { fetchScheduleDay } from "../../helper/Api";
import {
  delayPush,
  isIllegalOparation,
  checkDoneItem,
} from "../../helper/Util";
import ErrorDialog from "../common/ErrorDialog";
import TopBar from "../common/TopBar";

import CheckIcon from "@material-ui/icons/CheckCircleOutlineSharp";
import CancelIcon from "@material-ui/icons/BlockOutlined";
import { errors, getApiError } from "../../helper/Error";

// CSSスタイルを定義
const useStyles = makeStyles((theme) =>
  createStyles({
    header: {
      textAlign: "left",
      backgroundColor: theme.palette.background.default,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      top: theme.mixins.toolbar.minHeight,
    },
    state: {
      fontSize: 15,
    },
    progressBar: {
      width: theme.spacing(13),
      marginRight: theme.spacing(1),
    },
    progress: {
      display: "flex",
      alignItems: "center",
    },
    check: {
      color: green[500],
    },
    notCheck: {
      color: "#e8e8e8",
    },
    cancelItem: {
      backgroundColor: theme.palette.action.disabledBackground,
    },
    cancelIcon: {
      color: theme.palette.error.main,
    },
    cancelText: {
      color: theme.palette.action.disabled,
    },
    bottom: {
      bottom: 0,
    },
  })
);

export default function List() {
  const history = useHistory();
  const location = useLocation();
  const { selectedDate } = location.state ?? 0;

  const classes = useStyles();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [isOpenError, setIsOpenError] = useState(false);
  const errorRef = useRef({});

  const handleCloseError = useCallback(() => {
    setIsOpenError(false);

    if (errorRef.current === errors.APP_ILLEGAL_OPERATION) {
      history.push("/calendar");
    }
  }, [history]);

  useEffect(() => {
    const fn = async () => {
      setIsLoading(true);

      await fetchScheduleDay(selectedDate)
        .then((res) => {
          if (res.data.error.length === 0) setData(res.data.result);
          else throw res.data.error;
        })
        .catch((err) => {
          errorRef.current = err.code
            ? getApiError(err)
            : errors.APP_CONNECTION_FAILED;
          setIsOpenError(true);
        });

      setIsLoading(false);
    };

    if (isIllegalOparation(location)) {
      errorRef.current = errors.APP_ILLEGAL_OPERATION;
      setIsOpenError(true);
    } else {
      fn();
    }
  }, [location, selectedDate]);

  const getAllCount = (arr) => {
    const cloneArr = [...arr];
    const allCount = cloneArr.filter(
      (obj) => !(obj.state.match(/キャンセル/) || obj.lawtype === "gyoumu")
    ).length;
    return allCount;
  };

  const getDoneCount = (arr) => {
    const stateArr = arr.map((obj) => obj.state);
    const doneCount = stateArr.filter((state) => checkDoneItem(state)).length;
    return doneCount;
  };

  const Subheader = () => (
    <ListSubheader className={classes.header}>
      {get日付(selectedDate)}
      {isLoading ? null : (
        <div className={classes.progress}>
          <LinearProgress
            variant="determinate"
            value={
              getAllCount(data) === 0
                ? 0
                : (getDoneCount(data) / getAllCount(data)) * 100
            }
            className={classes.progressBar}
          />
          <Typography variant="overline">{`${getDoneCount(data)}/${getAllCount(
            data
          )}件`}</Typography>
        </div>
      )}
      <Divider className={classes.bottom} absolute />
    </ListSubheader>
  );

  const handleClickListItem = (userList) => {
    delayPush(history, {
      pathname: "/detail",
      state: {
        selectedDate: selectedDate,
        selectedName: userList.riyousha,
        selectedValue: `${RegExp(/\d+:\d+/).exec(userList.begintime)}-${RegExp(
          /\d+:\d+/
        ).exec(userList.endtime)} ${userList.servicename}`,
        timeRange: `${RegExp(/\d+:\d+/).exec(userList.begintime)}-${RegExp(
          /\d+:\d+/
        ).exec(userList.endtime)}`,
        serviceName: userList.servicename,
        serviceId: userList.service_id,
        beginend: userList.beginend,
        options: userList.options,
        position: userList.position,
        kakunin: userList.kakunin,
        state: userList.state,
        kirokuBeginTime: userList.kiroku_begintime,
        kirokuEndTime: userList.kiroku_endtime,
      },
    });
  };

  const ListItemCustom = ({ userData }) => {
    const time = `${RegExp(/\d+:\d+/).exec(userData.begintime)}-${RegExp(
      /\d+:\d+/
    ).exec(userData.endtime)}`;

    if (userData.state.match(/キャンセル/)) {
      return (
        <ListItem className={classes.cancelItem}>
          <ListItemText
            primary={userData.riyousha}
            secondary={`${time} ${userData.servicename}`}
            classes={{
              primary: classes.cancelText,
              secondary: classes.cancelText,
            }}
          />
          <CancelIcon className={classes.cancelIcon} />
        </ListItem>
      );
    } else if (userData.lawtype === "gyoumu") {
      return (
        <ListItem>
          <ListItemText primary={userData.servicename} secondary={time} />
        </ListItem>
      );
    } else {
      return (
        <ListItem button onClick={() => handleClickListItem(userData)}>
          <ListItemText
            primary={userData.riyousha}
            secondary={`${time} ${userData.servicename}`}
          />
          {checkDoneItem(userData.state) ? (
            <CheckIcon className={classes.check} />
          ) : (
            <CheckIcon className={classes.notCheck} />
          )}
        </ListItem>
      );
    }
  };

  const UserList = () =>
    data.map((userData, idx) => (
      <div key={idx}>
        <ListItemCustom userData={userData} />

        {data.length !== idx + 1 && <Divider />}
      </div>
    ));

  return (
    <>
      <TopBar isLoading={isLoading} />

      {isIllegalOparation(location) ? null : (
        <MuiList subheader={<Subheader />}>
          {isLoading ? null : <UserList />}
        </MuiList>
      )}

      <ErrorDialog
        open={isOpenError}
        onClose={handleCloseError}
        error={errorRef.current}
      />
    </>
  );
}
